// src/frontend_react/src/pages/UserDashboard/ResultsStep.js
import React from 'react';
import Papa from 'papaparse';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Card from '../../components/ui/Card/Card';
import Alert from '../../components/ui/Alert/Alert';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Icon from '../../components/ui/Icon/Icon';

// Local styles
import styles from './ResultsStep.module.css';

// Icons
import {
  FiCheckCircle,
  FiRotateCcw,
  FiAlertTriangle,
  FiXCircle,
  FiDownload,
  FiFileText,
} from 'react-icons/fi';

function ResultsStep({ manifest, onReset, isEmbedded = false }) {
  if (!manifest || !manifest.success) {
    const errorMessage =
      manifest?.message ||
      'Processing was not successful and no manifest is available.';
    return (
      <Box
        className={styles.resultsContainer}
        display="flex"
        justifyContent="center"
      >
        <Card
          title="Processing Failed"
          className={styles.resultsCard}
          titleIcon={<Icon as={FiXCircle} color="error" size="h3" />}
        >
          <Stack spacing="lg" alignItems="center" p="md">
            <Alert variant="error">{errorMessage}</Alert>
            <Button
              variant="primary"
              onClick={onReset}
              leftIcon={<Icon as={FiRotateCcw} />}
            >
              Start Over
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  const handleDownloadSkipped = () => {
    const skippedRecords =
      manifest.file_metadata?.skipped_records_preview || [];
    if (skippedRecords.length === 0) {
      alert('No skipped records data available in the manifest to download.');
      return;
    }

    const dataToDownload = skippedRecords.map((item) => item.original_row);
    const csvString = Papa.unparse(dataToDownload, { header: true });
    const blob = new Blob([`\uFEFF${csvString}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    const originalBaseName = manifest.original_filename.replace(
      /\.[^/.]+$/,
      '',
    );
    link.setAttribute(
      'download',
      `skipped_records_for_${originalBaseName}.csv`,
    );

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summaryItems = [
    {
      key: 'original_filename',
      label: 'Original File Processed',
      icon: <Icon as={FiFileText} />,
    },
    { key: 'deidentified_rows', label: 'Records Successfully Processed' },
    { key: 'skipped_rows', label: 'Records Skipped (e.g., Invalid IC)' },
  ];

  if (manifest.output_file_id) {
    summaryItems.splice(1, 0, {
      key: 'output_file_id',
      label: 'Central Registration ID',
    });
  }

  const content = (
    <Stack spacing="lg" p={isEmbedded ? '0' : 'md'}>
      {!isEmbedded && (
        <Typography variant="body1" color="secondary" align="center">
          The data file "<strong>{manifest.original_filename}</strong>" has been
          successfully processed, encrypted, and registered.
        </Typography>
      )}

      <Box className={styles.summaryDetailsBox}>
        <Stack as="ul" divider={<Box className={styles.summaryDivider} />}>
          {summaryItems.map((item) => {
            const value = manifest[item.key];
            if (value === undefined || value === null) return null;

            return (
              <li key={item.key} className={styles.summaryItem}>
                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  p="md"
                >
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    style={{ flexGrow: 1 }}
                  >
                    {item.label}:
                  </Typography>
                  <Typography
                    variant="body1"
                    color="primary"
                    fontWeight="semibold"
                  >
                    {String(value)}
                  </Typography>
                </HStack>
              </li>
            );
          })}
        </Stack>
      </Box>

      {manifest.skipped_rows > 0 && (
        <Alert
          variant="warning"
          title="Skipped Records Notice"
          icon={<Icon as={FiAlertTriangle} />}
        >
          <Stack spacing="md">
            <Typography>
              {manifest.skipped_rows} record(s) were skipped due to invalid or
              missing Identifier Column values. These records were not included
              in the secure output. You can download a preview of these skipped
              records for review.
            </Typography>
            <Box>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadSkipped}
                leftIcon={<Icon as={FiDownload} />}
              >
                Download Skipped Records Preview
              </Button>
            </Box>
          </Stack>
        </Alert>
      )}

      {!isEmbedded && (
        <HStack justifyContent="center">
          <Button
            variant="primary"
            onClick={onReset}
            leftIcon={<Icon as={FiRotateCcw} />}
          >
            Prepare Another Data File
          </Button>
        </HStack>
      )}
    </Stack>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <Box
      className={styles.resultsContainer}
      display="flex"
      justifyContent="center"
    >
      <Card
        title="Processing & Registration Complete!"
        className={styles.resultsCard}
        titleIcon={<Icon as={FiCheckCircle} color="success" size="h3" />}
      >
        {content}
      </Card>
    </Box>
  );
}

export default ResultsStep;
